import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { ApiConnectionString } from '../constants/ApiConntection'

export default function PracticeImages({ id, user }) {
  const headshotInputRef = useRef(null)
  const workspaceInputRef = useRef(null)

  const [headshotUrl, setHeadshotUrl] = useState('')
  const [workspaceUrl, setWorkspaceUrl] = useState('')

  const [headshotNoUpload, setHeadshotNoUpload] = useState(false)
  const [workspaceNoUpload, setWorkspaceNoUpload] = useState(false)

  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const fetchImageUrlIfExists = async (endpoint, setUrl) => {
    try {
      const response = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        responseType: 'blob',
        validateStatus: (status) => (status >= 200 && status < 300) || status === 204,
      })

      if (response.status === 204) {
        setUrl((prev) => {
          if (prev) URL.revokeObjectURL(prev)
          return ''
        })
        return
      }

      const objectUrl = URL.createObjectURL(response.data)
      setUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return objectUrl
      })
    } catch {
      setUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return ''
      })
    }
  }

  useEffect(() => {
    if (!id) return

    fetchImageUrlIfExists(
      `${ApiConnectionString}/api/practiceImage/HeadshotImage/${id}`,
      setHeadshotUrl
    )
    fetchImageUrlIfExists(
      `${ApiConnectionString}/api/practiceImage/WorkspaceImage/${id}`,
      setWorkspaceUrl
    )

    return () => {
      setHeadshotUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return ''
      })
      setWorkspaceUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev)
        return ''
      })
    }
  }, [id])

  useEffect(() => {
    if (headshotUrl) setHeadshotNoUpload(true)
  }, [headshotUrl])

  useEffect(() => {
    if (workspaceUrl) setWorkspaceNoUpload(true)
  }, [workspaceUrl])

  const refreshImages = async () => {
    if (!id) return

    await fetchImageUrlIfExists(
      `${ApiConnectionString}/api/practiceImage/HeadshotImage/${id}`,
      setHeadshotUrl
    )
    await fetchImageUrlIfExists(
      `${ApiConnectionString}/api/practiceImage/WorkspaceImage/${id}`,
      setWorkspaceUrl
    )
  }

  const upload = async (endpoint, file) => {
    if (!file) return

    setLoading(true)
    setMessage('')

    try {
      const formData = new FormData()
      formData.append('image', file)

      await axios.post(endpoint, formData, {
        headers: {
          Authorization: `Bearer ${user.token}`,
          'Content-Type': 'multipart/form-data',
        },
      })

      setMessage('Uploaded')
      await refreshImages()
    } catch (err) {
      setMessage(err?.response?.data || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const remove = async (endpoint, inputRef, setNoUpload) => {
    setLoading(true)
    setMessage('')

    try {
      await axios.delete(endpoint, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      })

      setMessage('Deleted')

      if (inputRef.current) inputRef.current.value = ''
      setNoUpload(false)

      await refreshImages()
    } catch (err) {
      setMessage(err?.response?.data || 'Something went wrong')
    } finally {
      setLoading(false)
    }
  }

  const onDeleteHeadshot = async () => {
    await remove(
      `${ApiConnectionString}/api/practiceImage/HeadshotImage`,
      headshotInputRef,
      setHeadshotNoUpload
    )
  }

  const onDeleteWorkspace = async () => {
    await remove(
      `${ApiConnectionString}/api/practiceImage/WorkspaceImage`,
      workspaceInputRef,
      setWorkspaceNoUpload
    )
  }

  return (
    <div className="container py-4">
      <h3 className="mb-2">Practice Images</h3>
      <p className="text-muted mb-4">
        Upload a professional headshot and a photo of your workspace for use on your website.
      </p>

      {message ? <div className="alert alert-info">{message}</div> : null}

      <div className="card mb-4">
        <div className="card-body">
          <h5 className="mb-1">Professional Headshot</h5>

          {headshotUrl ? (
            <div className="text-success mb-3 d-flex align-items-center gap-2">
              <span>✓</span>
              <span>Headshot uploaded</span>
            </div>
          ) : (
            <div className="text-muted mb-3">
              A clear photo of you, ideally with good lighting and a simple background.
            </div>
          )}

          {headshotUrl ? (
            <div className="mb-3">
              <img src={headshotUrl} alt="Headshot" className="img-fluid rounded border w-75" />
            </div>
          ) : null}

          {!headshotUrl ? (
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="headshotNoUpload"
                checked={headshotNoUpload}
                required
                onChange={(e) => {
                  const checked = e.target.checked
                  setHeadshotNoUpload(checked)
                  if (checked) {
                    if (headshotInputRef.current) headshotInputRef.current.value = ''
                  }
                }}
              />
              <label className="form-check-label" htmlFor="headshotNoUpload">
                I do not wish to upload img
              </label>
            </div>
          ) : null}

          <input
            ref={headshotInputRef}
            className="form-control mb-3"
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0] || null
              if (!file) return

              setHeadshotNoUpload(true)
              await upload(`${ApiConnectionString}/api/practiceImage/HeadshotImage`, file)

              if (headshotInputRef.current) headshotInputRef.current.value = ''
            }}
            disabled={loading || (headshotNoUpload && !headshotUrl)}
          />

          <div className="d-flex gap-2">
            {headshotUrl ? (
              <button
                className="btn btn-outline-danger"
                onClick={onDeleteHeadshot}
                disabled={loading}
                type="button"
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          <h5 className="mb-1">Workspace Photo</h5>

          {workspaceUrl ? (
            <div className="text-success mb-3 d-flex align-items-center gap-2">
              <span>✓</span>
              <span>Workspace photo uploaded</span>
            </div>
          ) : (
            <div className="text-muted mb-3">
              A photo of the space you work in, so clients can get a feel for the environment.
            </div>
          )}

          {workspaceUrl ? (
            <div className="mb-3">
              <img src={workspaceUrl} alt="Workspace" className="img-fluid rounded border w-75" />
            </div>
          ) : null}

          {!workspaceUrl ? (
            <div className="form-check mb-3">
              <input
                className="form-check-input"
                type="checkbox"
                id="workspaceNoUpload"
                checked={workspaceNoUpload}
                required
                onChange={(e) => {
                  const checked = e.target.checked
                  setWorkspaceNoUpload(checked)
                  if (checked) {
                    if (workspaceInputRef.current) workspaceInputRef.current.value = ''
                  }
                }}
              />
              <label className="form-check-label" htmlFor="workspaceNoUpload">
                I do not wish to upload img
              </label>
            </div>
          ) : null}

          <input
            ref={workspaceInputRef}
            className="form-control mb-3"
            type="file"
            accept="image/*"
            onChange={async (e) => {
              const file = e.target.files?.[0] || null
              if (!file) return

              setWorkspaceNoUpload(true)
              await upload(`${ApiConnectionString}/api/practiceImage/WorkspaceImage`, file)

              if (workspaceInputRef.current) workspaceInputRef.current.value = ''
            }}
            disabled={loading || (workspaceNoUpload && !workspaceUrl)}
          />

          <div className="d-flex gap-2">
            {workspaceUrl ? (
              <button
                className="btn btn-outline-danger"
                onClick={onDeleteWorkspace}
                disabled={loading}
                type="button"
              >
                Remove
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}
